// frontend/src/components/looks/SaveButton.js
// W3 — Save a Look to a collection. Account required to act.
// Uses a default "Saved Looks" collection (auto-created on first save).
//
// Props:
//   lookId   — Look._id (required)
//   apiBase  — optional API base override
//   compact  — render the compact icon+label variant (default true)

import React, { useState, useCallback, useEffect } from "react";
import { FaBookmark, FaRegBookmark } from "react-icons/fa";
import AuthRequiredModal from "../AuthRequiredModal";

const DEFAULT_API = process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
const DEFAULT_COLLECTION_NAME = "Saved Looks";

function SaveButton({ lookId, apiBase = DEFAULT_API, compact = true }) {
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showAuth, setShowAuth] = useState(false);

  const getToken = () => localStorage.getItem("ogestyle_token");

  // Find the default collection, creating it if missing.
  const ensureDefaultCollection = async (token) => {
    const listRes = await fetch(`${apiBase}/api/looks-social/collections`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const listData = await listRes.json();
    if (!listRes.ok) throw new Error(listData?.message || "Unable to load collections");
    const existing = (listData.collections || []).find(
      (c) => String(c.name).trim().toLowerCase() === DEFAULT_COLLECTION_NAME.toLowerCase(),
    );
    if (existing) return existing._id;
    const createRes = await fetch(`${apiBase}/api/looks-social/collections`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ name: DEFAULT_COLLECTION_NAME, isPublic: false }),
    });
    const createData = await createRes.json();
    if (!createRes.ok) throw new Error(createData?.message || "Unable to create collection");
    const created = createData.collection || createData;
    return created._id;
  };

  // Load saved status when authenticated.
  const loadState = useCallback(async () => {
    if (!lookId) return;
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${apiBase}/api/looks-social/looks/${lookId}/saved`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) return;
      setSaved(!!data.saved);
    } catch (err) {
      // Non-fatal.
    }
  }, [lookId, apiBase]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const toggle = async () => {
    setError("");
    const token = getToken();
    if (!token) {
      setShowAuth(true);
      return;
    }
    setBusy(true);
    try {
      if (saved) {
        // Unsave: unsave from any collection the look is saved in.
        const statusRes = await fetch(`${apiBase}/api/looks-social/looks/${lookId}/saved`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const statusData = await statusRes.json();
        const collectionIds = statusData.savedCollectionIds || [];
        for (const collectionId of collectionIds) {
          await fetch(`${apiBase}/api/looks-social/collections/${collectionId}/save/${lookId}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          });
        }
        setSaved(false);
      } else {
        const collectionId = await ensureDefaultCollection(token);
        const saveRes = await fetch(
          `${apiBase}/api/looks-social/collections/${collectionId}/save/${lookId}`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const saveData = await saveRes.json();
        if (!saveRes.ok) throw new Error(saveData?.message || "Unable to save look");
        setSaved(true);
      }
    } catch (err) {
      setError(err?.message || "Unable to save look");
    } finally {
      setBusy(false);
    }
  };

  const style = {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    border: "none",
    background: "transparent",
    color: saved ? "#1d4f80" : "#64748b",
    fontSize: compact ? "12px" : "13px",
    fontWeight: 600,
    cursor: busy ? "default" : "pointer",
    padding: "0",
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
  };

  return (
    <>
      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
        <button onClick={toggle} disabled={busy} aria-label={saved ? "Saved" : "Save"} style={style}>
          {saved ? <FaBookmark size={14} /> : <FaRegBookmark size={14} />}
          <span>{saved ? "Saved" : "Save"}</span>
        </button>
        {error && <span style={{ fontSize: "10px", color: "#b91c1c" }}>{error}</span>}
      </span>
      <AuthRequiredModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        intent="save this look to a collection"
        onAuthed={() => {
          setShowAuth(false);
          toggle();
        }}
      />
    </>
  );
}

export default SaveButton;
